// End-game sequence: scrolly, circuit flashes, PSI stream, story text and
// credits. Ported from ending.c.

import { rand } from './rng';
import { G } from './state';
import { Paint } from './mapgen';
import { getImg, getAssetScale } from '../render/assets';
import {
  screen,
  blitGame,
  fillSurface,
  DrawRect,
  DrawCircle,
  DrawCircleEx,
  draw_text,
} from '../render/surface';
import {
  BASE_TILE,
  SCREEN_W,
  SCREEN_H,
  SCREEN_CX,
  SCREEN_CY,
  PLAYERW,
  PLAYERH,
  S,
} from './constants';
import { physPal, markPalDirty } from '../render/palette';
import { present } from '../render/display';
import { nextTick } from './loop';
import { takeEvents } from './input';
import { MusicUpdate } from '../audio/index';
import { SND_CircuitRelease } from '../audio/index';
import { DrawLevel, DrawPlayer } from './game-draw';
import { executeScene } from '../render/scene';

const M_PI = Math.PI;
const tr = Math.trunc;

// ending_pal[256] mirrored as RGB bytes; UpdatePalette copies it to physPal.
const endPal = new Uint8Array(256 * 3);
function epSet(i: number, r: number, g: number, b: number): void {
  endPal[i * 3] = tr(r) & 255;
  endPal[i * 3 + 1] = tr(g) & 255;
  endPal[i * 3 + 2] = tr(b) & 255;
}
function epR(i: number): number {
  return endPal[i * 3];
}
function epG(i: number): number {
  return endPal[i * 3 + 1];
}
function epB(i: number): number {
  return endPal[i * 3 + 2];
}
function UpdatePalette(): void {
  physPal.set(endPal);
  markPalDirty();
}

function artifactSpr() {
  return getImg('dat/i/artifacts.png');
}

let credits_scroll = 0;

function EndingEvents(): boolean {
  G.player_room = 0;
  G.current_boss = 3;
  G.boss_fight_mode = 4;
  MusicUpdate();
  for (const ev of takeEvents()) {
    if (ev.down && ev.sym === 'ESCAPE') return true;
  }
  return false;
}

const SText = [
  'Merit released the locks on the PSI flowing through the Dome,',
  'releasing the flow of PSI into the atmosphere.',
  '',
  'The Orcus Dome was originally built to centralise the limited',
  'PSI available to everyone. However, this made the existing',
  'reserves more vulnerable to malicious PSI users',
  '',
  'While other PSI users initially resented Merit for his rash',
  'behaviour, they eventually adjusted to the decentralisation.',
  '',
  'Eventually, PSI users grew so adept at manipulating the',
  'diluted flows of PSI that they were capable of the same things',
  'as before. Each PSI user would keep their own individual',
  'reserves of PSI for when they needed to weild greater power,',
  'and the balance of power was restored.',
];

const STextV = [
  'Merit decided to assume the role of custodian over the Orcus',
  "Dome, in Wervyn Anixil's place. He resumed the experiments on",
  "PSI and found ways of making the Dome's remaining supply go as",
  'far as it could.',
  '',
  'Other PSI users were suspicious of MERIT, just as they were',
  'wary of Wervyn Anixil before him, but they soon adjusted.',
  '',
  'The balance of power was quickly restored, and stabilised for',
  'eternity due to the work of Wervyn Anixil and now MERIT.',
  '',
  '',
  '                      [[ BEST ENDING ]]',
  '',
  '',
];

const PText = [
  'Activating the seal quickly unblocked the ley lines and allowed',
  'PSI to flow through the Dome again. The remaining shadows were',
  'quickly flushed out.',
  '',
  "Wervyn Anixil's unconventional use of the PSI resulted in him",
  'being burned out and rendered powerless. Merit will see to it',
  'that he faces judgement for his crimes.',
  '',
  'Neither of the two PSI weapons housed within the Dome had been',
  'touched. However . . .',
];

const PTextV = [
  'Activating the seal quickly unblocked the ley lines and allowed',
  'PSI to flow through the Dome again. The remaining shadows were',
  'quickly flushed out.',
  '',
  'The traitor, who was never identified, perished in the Sealing.',
  'It soon became clear that the traitor had managed to betray and',
  'kill the real Wervyn Anixil during his experiments on the PSI.',
  'If the Agate Knife was never found, nobody would have been any',
  'the wiser, and things could have turned out very differently.',
  'However, there was one last thing for MERIT to do.',
];

// Faithful to ending.c: the "Wervyn" beta-testing line and every music credit
// are adjacent string literals, so C concatenates them into a single element.
const credits = [
  'Concept:                    Lancer-X/Asceai',
  'Game design:                Lancer-X/Asceai',
  'Graphics:                   Lancer-X/Asceai',
  'Programming:                Lancer-X/Asceai',
  'Sound Effects:              Various (public domain) sources',
  'Music:                      Various artists',
  'Beta testing:               Quasar',
  'Beta testing:               Terryn',
  'Beta testing:               Wervyn' +
    '"Ambient Light"       Vogue of Triton' +
    '"Battle of Ragnarok"  Frostbite' +
    '"Dragon Cave"         TICAZ' +
    ' cavern.xm             Unknown' +
    '"Caverns Boss"        Alexis Janson' +
    '"Forest Boss"         Alexis Janson' +
    '"Catacombs Boss"      Alexis Janson' +
    '"Fear 2"              Mick Rippon' +
    '"The Final Battle"    Goose/CéDA & iNVASiON' +
    '"Ice Frontier"        Skaven/FC' +
    '"KnarkLoader 1.0"     Rapacious' +
    '"RPG-Battle"          Cyn' +
    '"Metallic Forest"     Joseph Fox',
];

const pt_x = new Float32Array(500);
const pt_y = new Float32Array(500);
const pt_vx = new Float32Array(500);
const pt_vy = new Float32Array(500);
const pt_t = new Int32Array(500);

function InitParticleStorm(): void {
  for (let i = 0; i < 500; i++) {
    pt_x[i] = SCREEN_CX;
    pt_y[i] = SCREEN_H * 2;
    pt_vx[i] = (rand() % 101) / 33.333 - 1.5;
    pt_vy[i] = (rand() % 101) / 10.0 - 16.1;
    pt_t[i] = rand() % 100;
  }
}

function DrawSTextGeneric(t: number, txt: string[]): void {
  const offset = S(540) + tr(t / 2);
  t = tr((t * 350) / 500);
  const cl = 350 - t;
  for (let i = 0; i < 64; i++) DrawRect(0, i * S(15) - offset, SCREEN_W, S(15), tr(((64 - i) * cl) / 350));
  for (let i = 64; i < 128; i++) DrawRect(0, i * S(15) - offset, SCREEN_W, S(15), tr(((i - 64) * cl) / 350));

  if (t < 300) {
    for (let i = 0; i < 15; i++) {
      let c = 255 + i * 100 - t * 10;
      if (c < 0) c = 0;
      if (c > 255) c = 255;
      draw_text(S(68), S(150) + i * S(12), txt[i], 255 - c);
    }
  } else {
    for (let i = 0; i < 15; i++) {
      const c = 5 + (t - 300) * 5;
      draw_text(S(68), S(150) + i * S(12), txt[i], 255 - c);
    }
  }
  UpdatePalette();
  present();
}

function DrawSText(t: number): void {
  DrawSTextGeneric(t, SText);
}
function DrawSTextV(t: number): void {
  DrawSTextGeneric(t, STextV);
}

function DrawPText(t: number): void {
  for (let i = 0; i < 256; i++) epSet(i, i, i, tr((i * 3) / 4) + 64);

  const txt = G.player_shield !== 30 ? PText : PTextV;
  if (t < 300) {
    for (let i = 0; i < 10; i++) {
      let c = 255 + i * 100 - t * 10;
      if (c < 0) c = 0;
      if (c > 255) c = 255;
      draw_text(S(68), S(180) + i * S(12), txt[i], c);
    }
  } else {
    for (let i = 0; i < 10; i++) {
      const c = 5 + (t - 300) * 5;
      draw_text(S(68), S(180) + i * S(12), txt[i], c);
    }
  }

  for (let i = 0; i < 32 * 8; i++) {
    const x = (i % 32) * S(20);
    const y = tr(i / 32) * S(20);
    let c = 237 + tr(i / 32) * 2 + (rand() % (19 - tr(i / 32) * 2));
    DrawRect(x, y, S(20), S(20), c);
    c = 237 + tr(i / 32) * 2 + (rand() % (19 - tr(i / 32) * 2));
    DrawRect(x, S(460) - y, S(20), S(20), c);
  }
  UpdatePalette();
  present();
}

function DrawScrolly(t: number): void {
  let xp: number;
  let yp: number;
  if (t < 795) {
    xp = S(8192) - S(320) - S(3180) + t * S(4);
    yp = t * S(20);
  } else {
    xp = S(8192) - S(320) + (t - 795) * S(10);
    yp = S(795) * S(20) - (t - 795) * S(10);
  }

  let all_blue = 0;
  if (rand() % 10 === 9) all_blue = 1;
  for (let i = 0; i < 256; i++) {
    const bright = Math.sin(t / 10.0) * 0.2 + 0.4;
    const base = i * bright + 256 * (1.0 - bright);
    const mul = (all_blue === 0 ? 1 : 0) * 0.5 + 0.5;
    epSet(i, base * mul, base * mul, base);
  }
  DrawLevel(xp, yp, 0, 0);

  const v_radius = Math.sin(t / 10.0) * S(20) + S(100);

  for (let i = 0; i < 5; i++) {
    const x = rand() % SCREEN_W;
    const y = rand() % SCREEN_H;
    const r = (rand() % 500) + 100;
    DrawCircleEx(x, y, r + 2, r - 4, 128);
    DrawCircleEx(x, y, r, r - 2, 255);
  }

  for (let i = 0; i < 4; i++) {
    const a_dir = t / 10.0 + (M_PI * i) / 2;
    for (let j = 10; j >= 0; j--) {
      DrawCircleEx(
        SCREEN_CX + Math.cos(a_dir) * v_radius,
        SCREEN_CY + Math.sin(a_dir) * v_radius,
        S(22) + j * S(2),
        0,
        Math.abs(j - 3) * 15
      );
    }
    DrawCircleEx(SCREEN_CX + Math.cos(a_dir) * v_radius, SCREEN_CY + Math.sin(a_dir) * v_radius, S(20), 0, 0);
    blitGame(
      artifactSpr(),
      { x: (8 + i) * BASE_TILE, y: 0, w: BASE_TILE, h: BASE_TILE },
      screen,
      SCREEN_CX + Math.cos(a_dir) * v_radius - S(16),
      SCREEN_CY + Math.sin(a_dir) * v_radius - S(16),
      getAssetScale('dat/i/artifacts.png')
    );
  }
  UpdatePalette();
  present();
}

let cf_xpos = 0;
let cf_ypos = 0;
function DrawCircuitFlash(t: number, method: number): void {
  const circ = getImg('dat/i/circuits_1.png');
  if (t === 0) {
    if (method === 0) {
      cf_xpos = rand() % (SCREEN_W + 1);
      cf_ypos = rand() % (SCREEN_H + 1);
    } else {
      cf_xpos = SCREEN_CX;
      cf_ypos = SCREEN_CY;
    }
  }
  blitGame(circ, { x: cf_xpos, y: cf_ypos, w: SCREEN_W, h: SCREEN_H }, screen, 0, 0, getAssetScale('dat/i/circuits_1.png'));
  for (let i = 0; i < 256; i++) {
    let j: number;
    if (method === 0) {
      j = tr((i * t) / 4);
    } else {
      j = tr((i * t) / 8);
      if (t >= 20) j += t * 25;
    }
    if (j > 255) j = 255;
    epSet(i, j, j, j);
  }
  UpdatePalette();
  present();
}

function DrawStream(t: number): void {
  let scr_x = S(32);
  let scr_y = 0;
  for (let i = 0; i < 256; i++) {
    epSet(
      i,
      i,
      tr((i * 7) / 8) + 16 + Math.sin(t / 8) * 16,
      tr((i * 3) / 4) + 32 + Math.sin(t / 8) * 32
    );
  }
  if (t >= 300) {
    scr_x = S(32) + (rand() % S(32)) - (rand() % S(32));
    scr_y = rand() % S(8);
  }
  if (t < 10) scr_y = S(20) - t * S(2);

  DrawLevel(scr_x, scr_y, 0, 0);
  DrawPlayer(SCREEN_CX - PLAYERW / 2 - scr_x, SCREEN_CY - PLAYERH / 2 - scr_y, 0, 0);

  const streamspr = getImg('dat/i/stream.png');
  for (let i = 0; i < 7; i++) {
    const strm_scrl = (t * S(20)) % S(128);
    const dx = 0 - strm_scrl - scr_x + S(128) * i;
    const dy = S(19) - scr_y;
    blitGame(streamspr, null, screen, dx, dy, getAssetScale('dat/i/stream.png'));
  }

  const glitter = getImg('dat/i/glitter.png');
  for (let i = 0; i < 20; i++) {
    blitGame(
      glitter,
      { x: (rand() % 3) * BASE_TILE, y: 0, w: BASE_TILE, h: BASE_TILE },
      screen,
      (rand() % (SCREEN_W + S(32))) - S(32),
      (rand() % S(124)) + S(3),
      getAssetScale('dat/i/glitter.png')
    );
  }

  if (t > 250 && t < 300) {
    if (t === 251) SND_CircuitRelease(1000);
    DrawCircle(SCREEN_CX + S(32) - scr_x, SCREEN_CY - scr_y, (t - 254) * S(10), 255);
    DrawCircle(SCREEN_CX + S(32) - scr_x, SCREEN_CY - scr_y, (t - 252) * S(10), 225);
    DrawCircle(SCREEN_CX + S(32) - scr_x, SCREEN_CY - scr_y, (t - 250) * S(10), 195);
  }
  UpdatePalette();
  present();
}

function RunParticleStorm(offset: number): void {
  for (let i = 0; i < 64; i++) DrawRect(0, i * S(15) - offset, SCREEN_W, S(15), 64 - i);

  const glitter = getImg('dat/i/glitter.png');
  for (let i = 0; i < 500; i++) {
    if (pt_t[i] > 0) {
      pt_t[i]--;
    } else {
      pt_vy[i] += 0.1;
      pt_x[i] += pt_vx[i];
      pt_y[i] += pt_vy[i];
    }
    blitGame(
      glitter,
      { x: (rand() % 3) * BASE_TILE, y: 0, w: BASE_TILE, h: BASE_TILE },
      screen,
      tr(pt_x[i]) - S(16),
      tr(pt_y[i]) - S(16) - offset,
      getAssetScale('dat/i/glitter.png')
    );
  }

  for (let i = 0; i < 128; i++) epSet(i, i * 2, i * 2, 0);
  for (let i = 128; i < 256; i++) epSet(i, 255, 255, (i - 128) * 2 + 1);
  UpdatePalette();
  present();
}

function DrawCredits(): void {
  const n_credits = credits.length;
  const finish_point = 400 + n_credits * 50;

  fillSurface(screen, 0);

  if (credits_scroll >= finish_point + 80) {
    blitGame(
      getImg(G.player_shield === 30 ? 'dat/i/true_end.png' : 'dat/i/theend.png'),
      null,
      screen,
      0,
      0,
      getAssetScale(G.player_shield === 30 ? 'dat/i/true_end.png' : 'dat/i/theend.png')
    );
  } else {
    blitGame(getImg('dat/i/fin.png'), null, screen, S(384), S(352), getAssetScale('dat/i/fin.png'));
    for (let i = 0; i < n_credits; i++) {
      const ypos = S(800) + i * S(100) - credits_scroll * 2;
      if (ypos >= 0 && ypos < SCREEN_H) {
        const c = 255 - Math.abs(ypos - SCREEN_CY);
        draw_text(S(120), ypos, credits[i], c);
      }
    }
  }

  for (let i = 0; i < 128; i++) epSet(i, 0, i, i * 2);
  for (let i = 128; i < 256; i++) epSet(i, (i - 128) * 2 + 1, i, 255);

  if (credits_scroll < 80) {
    for (let i = 0; i < 256; i++)
      epSet(i, tr((epR(i) * credits_scroll) / 80), tr((epG(i) * credits_scroll) / 80), tr((epB(i) * credits_scroll) / 80));
  }
  if (credits_scroll >= finish_point && credits_scroll < finish_point + 80) {
    for (let i = 0; i < 256; i++)
      epSet(
        i,
        tr((epR(i) * (finish_point + 80 - credits_scroll)) / 80),
        tr((epG(i) * (finish_point + 80 - credits_scroll)) / 80),
        tr((epB(i) * (finish_point + 80 - credits_scroll)) / 80)
      );
  }
  if (credits_scroll >= finish_point + 80 && credits_scroll < finish_point + 160) {
    for (let i = 0; i < 256; i++)
      epSet(
        i,
        tr((epR(i) * (credits_scroll - (finish_point + 80))) / 80),
        tr((epG(i) * (credits_scroll - (finish_point + 80))) / 80),
        tr((epB(i) * (credits_scroll - (finish_point + 80))) / 80)
      );
  }

  credits_scroll++;
  UpdatePalette();
  present();
}

export async function ShowEnding(): Promise<void> {
  for (let i = 0; i < 500; i += 1) {
    executeScene(() => {
      if (i % 60 >= 24 && i % 60 < 34) DrawCircuitFlash((i % 60) - 24, 0);
      else DrawScrolly(i);
    });
    await nextTick();
    if (EndingEvents()) return;
  }
  for (let i = 0; i < 30; i++) {
    executeScene(() => DrawCircuitFlash(i, 1));
    await nextTick();
    if (EndingEvents()) return;
  }
  executeScene(() => fillSurface(screen, 255));
  for (let i = 0; i < 350; i++) {
    executeScene(() => DrawPText(i));
    await nextTick();
    if (EndingEvents()) return;
  }

  Paint(0, 0, 22, 27, 'dat/d/cstream.loc');

  if (G.player_shield < 30) {
    for (let i = 0; i < 400; i++) {
      executeScene(() => DrawStream(i));
      await nextTick();
      if (EndingEvents()) return;
    }
    InitParticleStorm();
    for (let i = 0; i < 240; i++) {
      executeScene(() => RunParticleStorm(S(240) - i));
      await nextTick();
      if (EndingEvents()) return;
    }
    for (let i = 0; i < 60; i++) {
      executeScene(() => RunParticleStorm(0));
      await nextTick();
      if (EndingEvents()) return;
    }
    for (let i = 0; i < 180; i++) {
      executeScene(() => RunParticleStorm(i * 3));
      await nextTick();
      if (EndingEvents()) return;
    }
    for (let i = 0; i < 500; i++) {
      executeScene(() => DrawSText(i));
      await nextTick();
      if (EndingEvents()) return;
    }
  } else {
    for (let i = 0; i < 250; i++) {
      executeScene(() => DrawStream(i));
      await nextTick();
      if (EndingEvents()) return;
    }
    for (let i = 0; i < 500; i++) {
      executeScene(() => DrawSTextV(i));
      await nextTick();
      if (EndingEvents()) return;
    }
  }

  credits_scroll = 0;
  for (;;) {
    executeScene(() => DrawCredits());
    await nextTick();
    if (EndingEvents()) return;
  }
}
